import { ref, computed } from 'vue';
import { defineStore } from 'pinia';
import { gql } from '@apollo/client/core';
import { apolloClient, setAuthToken, setLocale, setVersion } from '@/lib/apollo';
import { safeResetApolloStore } from '@/lib/apolloStoreReset';
import { BASE_URL, DEFAULT_VERSION } from '@/lib/constants';
import { DEFAULT_LOCALE, detectSystemLocale, i18n, normalizeLocale, setI18nLanguage } from '@/composables/i18n';
import { createAccount, type CreateAccountInput } from '@/actions/auth/createAccount';
import { Preferences } from '@capacitor/preferences';
import type { Me } from '@/types/graphql';
import {useAppVersion} from "@/composables/useAppVersion";
import { useAppSettingsStore } from '@/stores/appSettings';
import { getAppPlatform } from '@/lib/platform';
import { setSentryUser, clearSentryUser } from '@/lib/setContexts';

const TOKEN_KEY = 'auth_token';
const USER_KEY = 'auth_user';

type AuthUser = Pick<Me, 'id' | 'name' | 'email' | 'locale' | 'profilePhotoUrl'>;

type Credentials = {
  email: string;
  password: string;
};

// ─── Storage helpers ──────────────────────────────────────────────────────────

async function readStoredValue(key: string): Promise<string | null> {
  try {
    const { value } = await Preferences.get({ key });
    if (value !== null) return value;
  } catch {
    // fall through to localStorage
  }

  if (typeof window !== 'undefined') {
    try {
      return window.localStorage.getItem(key);
    } catch {
      return null;
    }
  }

  return null;
}

async function writeStoredValue(key: string, value: string | null) {
  if (value === null) {
    try { await Preferences.remove({ key }); } catch { /* ignore */ }
    if (typeof window !== 'undefined') {
      try { window.localStorage.removeItem(key); } catch { /* ignore */ }
    }
    return;
  }

  try { await Preferences.set({ key, value }); } catch { /* ignore */ }
  if (typeof window !== 'undefined') {
    try { window.localStorage.setItem(key, value); } catch { /* ignore */ }
  }
}

function parseStoredUser(value: string | null): AuthUser | null {
  if (!value) return null;
  try {
    return JSON.parse(value) as AuthUser;
  } catch {
    return null;
  }
}

function computeLoginEndpoint(): string {
  return BASE_URL ? `${BASE_URL}/auth/login` : '/login';
}

// ─── Store ────────────────────────────────────────────────────────────────────

export const useAuthStore = defineStore('auth', () => {
  const token = ref<string>('');
  const user = ref<AuthUser | null>(null);
  const isReady = ref(false);
  const locale = ref(DEFAULT_LOCALE);
  const fullVersion = useAppVersion();

  const isAuthenticated = computed(() => Boolean(token.value));

  // ── Locale ──────────────────────────────────────────────────────────────────

  async function changeLocale(newLocale: string) {
    const normalizedLocale = normalizeLocale(newLocale);

    locale.value = normalizedLocale;
    setLocale(normalizedLocale);

    if (user.value) {
      user.value = { ...user.value, locale: normalizedLocale };
      await writeStoredValue(USER_KEY, JSON.stringify(user.value));
    }

    setI18nLanguage(i18n, normalizedLocale);

    // Locale contract: update i18n/auth headers only; visible screens can refetch as needed.
  }

  // ── App open ─────────────────────────────────────────────────────────────────

  async function handleAppOpen() {
    
    const platform = getAppPlatform();
    
    await apolloClient.mutate({
      mutation: gql`
        mutation handleAppOpen($platform: String!, $deviceInfo: Mixed!) {
          handleAppOpen(platform: $platform, deviceInfo: $deviceInfo) {
            id
          }
        }
      `,
      variables: {
        platform,
        deviceInfo: {},
      },
    });
  }

  // ── Initialize (restore from storage) ───────────────────────────────────────

  async function initialize() {
    const [tokenValue, userValue] = await Promise.all([
      readStoredValue(TOKEN_KEY),
      readStoredValue(USER_KEY),
    ]);
    
    if (userValue) {
      user.value = parseStoredUser(userValue);
    }

    if (tokenValue) {
      token.value = tokenValue;
      setAuthToken(tokenValue);
      setVersion(fullVersion.value);
    }

    // First-time users should see auth pages in system language.
    const resolvedLocale = normalizeLocale(user.value?.locale ?? detectSystemLocale());
    locale.value = resolvedLocale;
    setLocale(resolvedLocale);
    setI18nLanguage(i18n, resolvedLocale);

    // ✅ restore Sentry user context on page refresh
    if (user.value) {
        setSentryUser({ id: user.value.id, email: user.value.email, name: user.value.name });
    }
    
    isReady.value = true;
  }

  // ── Login ────────────────────────────────────────────────────────────────────

  async function login(credentials: Credentials) {
    const endpoint = computeLoginEndpoint();
    let response: Response | null = null;

    try {
      response = await fetch(endpoint, {
        method: 'POST',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-App-Version': DEFAULT_VERSION,
            'X-App-Platform': getAppPlatform(),
        },
        body: JSON.stringify(credentials),
      });
    } catch {
      throw new Error('Unable to reach the authentication server.');
    }

    const responseBody = (await response.json().catch(() => null)) as Record<string, any> | null;
    
    if (!response.ok) {
      const message =
        responseBody?.errors?.email?.[0] ??
        responseBody?.message ??
        'The provided credentials are incorrect.';
      throw new Error(message);
    }

    const payload = responseBody ?? {};

    if (!payload.token) {
      throw new Error('Authentication succeeded but no token was returned.');
    }

    token.value = payload.token;
    user.value = payload.user ?? null;

    setAuthToken(token.value);
    setLocale(normalizeLocale(user.value?.locale ?? DEFAULT_LOCALE));
    setVersion(fullVersion.value);

    //load settings immediately after login to ensure we have the latest constraints for validation
    await useAppSettingsStore().refreshFromApi();

    await Promise.all([
      writeStoredValue(TOKEN_KEY, token.value),
      writeStoredValue(USER_KEY, JSON.stringify(user.value)),
    ]);

    
    try {
      await handleAppOpen();
    } catch (e) {
      console.error('Failed to handle app open after login:', e);
    }
    
    // Sync locale with the user's preference
    if (user.value?.locale) {
      await changeLocale(user.value.locale);
    }

    if(user.value) {
      setSentryUser({ id: user.value.id, email: user.value.email, name: user.value.name })
    }

    console.log('Welcome back, ' + (user.value?.name ?? 'user') + '!');
  }

  // ── Register ─────────────────────────────────────────────────────────────────

  async function register(input: CreateAccountInput) {
    return await createAccount({
      ...input,
      locale: normalizeLocale(input.locale || locale.value),
    });
  }

  // ── Logout ───────────────────────────────────────────────────────────────────

  async function logout() {
    token.value = '';
    user.value = null;
    setAuthToken(undefined);

    await Promise.all([
      writeStoredValue(TOKEN_KEY, null),
      writeStoredValue(USER_KEY, null),
    ]);

    try {
      await safeResetApolloStore();
      clearSentryUser();
    } catch (e) {
      console.error('Failed to clear Apollo store on logout:', e);
    }
  }

  // ── Attempt restore ───────────────────────────────────────────────────────────

  async function attemptRestore(): Promise<boolean> {
    await initialize();

    if (isAuthenticated.value) {
      try {
        await handleAppOpen();
      } catch (e) {
        console.error('Failed to handle app open on restore:', e);
      }
    }

    return isAuthenticated.value;
  }

  return {
    token,
    user,
    isReady,
    locale,
    isAuthenticated,
    changeLocale,
    handleAppOpen,
    initialize,
    login,
    register,
    logout,
    attemptRestore,
  };
});